import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { apiClient } from '@/api/axiosClient';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import { INDIAN_STATES, stateNameForCode } from '@/lib/indianStates';
import type { CarrierDto, CustomerDto } from '@/types/materials';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    gstin: z.string().nullable(),
    address: z.string().nullable(),
    contactPerson: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    status: z.enum(['Active', 'Inactive']),
    stateCode: z.string().nullable(),
    stateName: z.string().nullable(),
    preferredCarrierId: z.string().nullable(),
    deliveryInstructions: z.string().nullable(),
  });
}

export type CustomerFormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: CustomerFormValues = {
  code: '', name: '', gstin: null, address: null, contactPerson: null, phone: null, email: null, status: 'Active',
  stateCode: null, stateName: null, preferredCarrierId: null, deliveryInstructions: null,
};

interface CustomerFormDialogProps {
  open: boolean;
  customer: CustomerDto | null;
  onClose: () => void;
  onSubmit: (values: CustomerFormValues) => void;
  loading?: boolean;
}

export default function CustomerFormDialog({ open, customer, onClose, onSubmit, loading }: CustomerFormDialogProps) {
  const { t } = useTranslation('customerMaster');
  const schema = buildSchema(t);
  const [tab, setTab] = useState(0);

  const { data: carriers = [] } = useQuery({
    queryKey: ['carriers'],
    queryFn: async () => (await apiClient.get<CarrierDto[]>('/carriers')).data,
    enabled: open,
  });

  const { control, handleSubmit, reset, setValue } = useForm<CustomerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      setTab(0);
      reset(
        customer
          ? {
              code: customer.code, name: customer.name, gstin: customer.gstin, address: customer.address,
              contactPerson: customer.contactPerson, phone: customer.phone, email: customer.email,
              status: customer.status as 'Active' | 'Inactive',
              stateCode: customer.stateCode, stateName: customer.stateName,
              preferredCarrierId: customer.preferredCarrierId, deliveryInstructions: customer.deliveryInstructions,
            }
          : DEFAULTS,
      );
    }
  }, [open, customer, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{customer ? customer.name : t('addCustomer')}</DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={t('tabs.general')} />
        <Tab label={t('tabs.logistics')} />
      </Tabs>
      <DialogContent>
        {tab === 0 && (
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.code')} fullWidth disabled={Boolean(customer)} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="gstin" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.gstin')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="contactPerson" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.contactPerson')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="phone" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.phone')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="email" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.email')} fullWidth type="email" />} />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.address')} fullWidth multiline rows={2} />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t('form.status')} fullWidth>
                  <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                  <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
        </GridContainer>
        )}

        {tab === 1 && (
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="stateCode"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label={t('form.state')}
                  fullWidth
                  sx={{ mt: 1 }}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const code = e.target.value || null;
                    field.onChange(code);
                    setValue('stateName', stateNameForCode(code));
                  }}
                >
                  <MenuItem value="">{t('form.stateNone')}</MenuItem>
                  {INDIAN_STATES.map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="preferredCarrierId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label={t('form.preferredCarrier')} value={field.value ?? ''} onChange={field.onChange} fullWidth
                  options={[{ value: '', label: t('form.preferredCarrierNone') }, ...carriers.map((c) => ({ value: c.id, label: c.name }))]}
                />
              )}
            />
          </GridItem>
          <GridItem xs={2}>
            <Controller
              name="deliveryInstructions"
              control={control}
              render={({ field }) => (
                <TextField {...field} value={field.value ?? ''} label={t('form.deliveryInstructions')} fullWidth multiline rows={2} />
              )}
            />
          </GridItem>
        </GridContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveCustomer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
